import { axiosBase } from '@/services/axios-base';
import {
  MemberPeerEvaluationDTO,
  ProjectPeerEvaluationDetailDTO,
  UserDTO,
  UserEngagementDTO,
  UserAdjustIndividualDropsDTO,
  UserEngagementReceivedDrops,
  UserEngagementRequestDTO,
  UserProfileDTO,
  UserContributionLevelDTO,
  ContributorReportDto,
  ContributorReportRequestDto
} from '@/models';
import { getReceivedDropsFromProjectPEs } from '@/services/project-drops';

export const fetchCurrentUserData = (): Promise<{ data: UserDTO }> => {
  return axiosBase.get('/api/currentLogin');
};

const fetchUsers = (projectId?: number): Promise<{ data: UserDTO[] }> => {
  return axiosBase.get('/api/users', {
    params: {
      projectId
    }
  });
};

export const fetchKMSUsers = (): Promise<{ data: UserDTO[] }> => {
  return axiosBase.get('/api/kms-users');
};

export const syncUsers = (): Promise<{ data: UserDTO[] }> => {
  return axiosBase.get('/api/user/sync');
};

export const transferUser = (targetId: string): Promise<{ data: string }> => {
  return axiosBase.put(`/api/organization/users/${targetId}`);
};

export const fetchImpersonate = (targetId: string): Promise<{ data: string }> => {
  return axiosBase.get(`/api/impersonate?targetId=${targetId}`);
};

const fetchUserProfile = (): Promise<{ data: UserProfileDTO }> => {
  return axiosBase.get(`/api/user/profile`);
};

export const getUserDisplayInfo = (user: UserDTO): string => {
  return `${user.fullName} - ${user.code}`;
};

export const fetchUserEvaluationDetail = (
  projectAssessmentId: number,
  userId: number
): Promise<{
  data: ProjectPeerEvaluationDetailDTO;
}> => {
  return axiosBase.get(`/api/assessment/${projectAssessmentId}/user/${userId}/evaluationDetail`);
};

export const getUserEngagementsReceivedDrops = (
  projectAssessmentId: number
): Promise<{ data: UserEngagementReceivedDrops[] }> => {
  return axiosBase.get(`/api/assessment/${projectAssessmentId}/userEngagement/receivedDrops`);
};

//#region adjust-individual-drops
export const buildUserEngagementsFromUserAdjustIndividualDrops = (
  userAdjustIndividualDrops: UserAdjustIndividualDropsDTO[],
  confirm: boolean
): UserEngagementRequestDTO[] => {
  return userAdjustIndividualDrops.map(userAdjustIndividualDrop => ({
    userId: userAdjustIndividualDrop.user.id,
    userEngagementRole: userAdjustIndividualDrop.userEngagementRole,
    contribution: userAdjustIndividualDrop.contribution,
    adjustedDrops: userAdjustIndividualDrop.adjustedDrops,
    givingDrops: userAdjustIndividualDrop.givingDrops,
    reasonForAdjustment: userAdjustIndividualDrop.reasonForAdjustment,
    peerEvaluationStatus: userAdjustIndividualDrop.peerEvaluationStatus,
    contributionDrops: userAdjustIndividualDrop.contributionDrops,
    individualDrops: confirm ? userAdjustIndividualDrop.individualDrops : undefined,
    adjustedBy: userAdjustIndividualDrop.adjustedBy
  }));
};

export const buildUserAdjustIndividualDrops = (
  userEngagements: UserEngagementDTO[],
  peerEvaluations: MemberPeerEvaluationDTO[]
): UserAdjustIndividualDropsDTO[] => {
  return userEngagements.map(userEngagement => {
    const receivedDrops = getReceivedDropsFromProjectPEs(userEngagement.user.id, peerEvaluations);
    const drops = userEngagement.contributionDrops - userEngagement.givingDrops;
    const summaryDrops = drops + receivedDrops;
    const finalIndividualDrops = userEngagement.adjustedDrops > 0 ? drops + userEngagement.adjustedDrops : summaryDrops;
    return {
      user: userEngagement.user,
      userEngagementRole: userEngagement.userEngagementRole,
      contribution: userEngagement.contribution,
      contributionDrops: userEngagement.contributionDrops,
      givingDrops: userEngagement.givingDrops,
      receivedDrops: receivedDrops,
      summaryDrops: summaryDrops,
      adjustedDrops: userEngagement.adjustedDrops ?? 0,
      individualDrops: finalIndividualDrops,
      reasonForAdjustment: userEngagement.reasonForAdjustment,
      peerEvaluationStatus: userEngagement.peerEvaluationStatus,
      adjustedBy: userEngagement.adjustedBy
    };
  });
};

export const saveAdjustReceivedDrops = (
  projectAssessmentId: number | undefined,
  confirm: boolean,
  userAdjustIndividualDrops?: UserAdjustIndividualDropsDTO[],
  userEngagements?: UserEngagementRequestDTO[]
): Promise<{ data: UserEngagementDTO[] }> => {
  const body = userAdjustIndividualDrops
    ? buildUserEngagementsFromUserAdjustIndividualDrops(userAdjustIndividualDrops, confirm)
    : userEngagements;

  return axiosBase.post(
    `/api/assessment/${projectAssessmentId}/userEngagements/adjustedDrops?confirm=${confirm}`,
    body
  );
};
//#endregion

export const userInLegendList = (): Promise<{ data: UserContributionLevelDTO[] }> => {
  return axiosBase.get('/api/contributionLevels/legend');
};

export const fetchContributorsReport = (
  body: ContributorReportRequestDto
): Promise<{ data: ContributorReportDto[] }> => {
  return axiosBase.post('/api/report/contributors', body);
};

export { fetchUsers, fetchUserProfile };
